import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  createEtapaPlantilla,
  ETAPAS_PLANTILLA_QUERY_KEY,
  listEtapasPlantilla,
  softDeleteEtapaPlantilla,
  updateEtapaPlantilla,
  type EtapaPlantilla,
} from '@/shared/api/controllers/importacionEtapasPlantilla';
import { ApiError } from '@/shared/api/client';
import type { EntityViewProps } from '../../types';

const entero = (min: number, max: number, msg: string) =>
  z.string().trim().refine((v) => /^\d+$/.test(v) && Number(v) >= min && Number(v) <= max, msg);

const schema = z.object({
  nombre: z.string().trim().min(1, 'Ingresá el nombre de la etapa').max(80, 'Hasta 80 caracteres'),
  orden: entero(0, 1000, 'Ingresá un número entero (0 o más)'),
  dias: entero(0, 3650, 'Ingresá los días (0 o más)'),
  avisarDiasAntes: entero(0, 365, 'Ingresá los días de aviso (0 o más)'),
  esArribo: z.boolean(),
  activo: z.boolean(),
});
type Values = z.infer<typeof schema>;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: EtapaPlantilla }
  | { kind: 'delete'; item: EtapaPlantilla };

const mensaje = (err: unknown, fallback: string) =>
  err instanceof ApiError && err.message ? err.message : err instanceof Error ? err.message : fallback;

/**
 * Configuración → Etapas de importación: la plantilla del ciclo de vida
 * (fabricación, tránsito, aduana…) que se copia a cada importación nueva. Una
 * sola etapa activa es "el arribo": define la fecha probable de llegada.
 */
export function EtapasImportacionEntity({ search, filterValue, onStatus, openCreate, onCreateHandled }: EntityViewProps) {
  const queryClient = useQueryClient();
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: ETAPAS_PLANTILLA_QUERY_KEY,
    queryFn: listEtapasPlantilla,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });
  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !filterValue || r.status === filterValue)
      .filter((r) => !q || r.nombre.toLowerCase().includes(q))
      .sort((a, b) => a.orden - b.orden || Number(a.id) - Number(b.id));
  }, [rows, search, filterValue]);

  // Lo que se copia a una importación nueva: las activas, en orden. La fecha
  // probable es el fin del arribo, así que cuenta hasta esa etapa inclusive.
  const duracion = useMemo(() => {
    const activas = rows
      .filter((r) => r.status === 'Activo')
      .sort((a, b) => a.orden - b.orden || Number(a.id) - Number(b.id));
    const total = activas.reduce((t, r) => t + r.dias, 0);
    const iArribo = activas.findIndex((r) => r.esArribo);
    const arribo =
      iArribo < 0
        ? null
        : { nombre: activas[iArribo].nombre, dias: activas.slice(0, iArribo + 1).reduce((t, r) => t + r.dias, 0) };
    return { total, arribo };
  }, [rows]);

  const columns: CrudColumn<EtapaPlantilla>[] = [
    { header: 'Status', width: 'w-28', render: (r) => <StatusBadge status={r.status} /> },
    { header: 'Orden', width: 'w-20', render: (r) => <span className="tabular-nums">{r.orden}</span> },
    { header: 'Etapa', render: (r) => <span className="font-medium text-foreground">{r.nombre}</span> },
    { header: 'Días', render: (r) => <span className="tabular-nums">{r.dias}</span> },
    { header: 'Avisar antes', render: (r) => <span className="tabular-nums">{r.avisarDiasAntes} días</span> },
    { header: 'Arribo', render: (r) => (r.esArribo ? 'Sí' : <span className="text-muted-foreground">—</span>) },
  ];

  const close = () => setMode({ kind: 'closed' });
  const done = (msg: string) => {
    queryClient.invalidateQueries({ queryKey: ETAPAS_PLANTILLA_QUERY_KEY });
    close();
    onStatus({ kind: 'success', message: msg });
  };
  const fail = (msg: string) => {
    close();
    onStatus({ kind: 'error', message: msg });
  };

  const del = useMutation({
    mutationFn: (item: EtapaPlantilla) => softDeleteEtapaPlantilla(item.id),
    onSuccess: () => done('La etapa se dio de baja.'),
    onError: (err) => fail(mensaje(err, 'No se pudo dar de baja la etapa.')),
  });

  const canEdit = useIsAdminTier();
  return (
    <>
      <div className="flex h-full min-h-0 flex-col gap-3">
        <div className="min-h-0 flex-1">
          <CrudTable
            hideActions={!canEdit}
            rows={filtered}
            columns={columns}
            loading={isPending}
            error={error instanceof Error ? error : null}
            onRetry={() => refetch()}
            emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin etapas cargadas.'}
            onEdit={(r) => setMode({ kind: 'edit', item: r })}
            onDelete={(r) => setMode({ kind: 'delete', item: r })}
            canDelete={(r) => r.status === 'Activo'}
            deleteDisabledTitle={() => 'El registro ya está inactivo'}
            isBackgroundFetching={isFetching}
          />
        </div>
        {rows.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {duracion.arribo && duracion.arribo.dias !== duracion.total && (
              <div className="inline-flex items-center gap-3 rounded-lg border bg-card px-4 py-2 text-sm">
                <span className="text-muted-foreground">Hasta el arribo ({duracion.arribo.nombre})</span>
                <span className="font-semibold tabular-nums text-foreground">{duracion.arribo.dias} días</span>
              </div>
            )}
            <div className="inline-flex items-center gap-3 rounded-lg border bg-card px-4 py-2 text-sm">
              <span className="text-muted-foreground">Duración total (etapas activas)</span>
              <span className="font-semibold tabular-nums text-foreground">{duracion.total} días</span>
            </div>
          </div>
        )}
      </div>

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <EtapaDialog
          item={mode.kind === 'edit' ? mode.item : null}
          proximoOrden={rows.reduce((m, r) => Math.max(m, r.orden), 0) + 1}
          onClose={close}
          onSuccess={done}
          onError={fail}
        />
      )}

      <Dialog open={mode.kind === 'delete'} onOpenChange={(o) => !o && !del.isPending && close()}>
        <DialogContent className="max-w-md" processing={del.isPending && 'Dando de baja…'}>
          <DialogHeader>
            <DialogTitle>Dar de baja etapa</DialogTitle>
            <DialogDescription>
              {mode.kind === 'delete' &&
                `"${mode.item.nombre}" deja de copiarse a las importaciones nuevas. Las importaciones ya creadas conservan sus etapas.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={del.isPending}>
              Cancelar
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={del.isPending}
              onClick={() => mode.kind === 'delete' && del.mutate(mode.item)}
            >
              Dar de baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EtapaDialog({
  item,
  proximoOrden,
  onClose,
  onSuccess,
  onError,
}: {
  item: EtapaPlantilla | null;
  proximoOrden: number;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item
      ? {
          nombre: item.nombre,
          orden: String(item.orden),
          dias: String(item.dias),
          avisarDiasAntes: String(item.avisarDiasAntes),
          esArribo: item.esArribo,
          activo: item.status === 'Activo',
        }
      : { nombre: '', orden: String(proximoOrden), dias: '', avisarDiasAntes: '3', esArribo: false, activo: true },
  });
  const mutation = useMutation({
    mutationFn: (v: Values) => {
      const payload = {
        nombre: v.nombre.trim(),
        orden: Number(v.orden),
        dias: Number(v.dias),
        avisarDiasAntes: Number(v.avisarDiasAntes),
        esArribo: v.esArribo,
        status: v.activo ? ('Activo' as const) : ('Inactivo' as const),
      };
      return item ? updateEtapaPlantilla(item.id, payload) : createEtapaPlantilla(payload);
    },
    onSuccess: () => onSuccess(item ? 'La etapa se actualizó correctamente.' : 'La etapa se creó correctamente.'),
    onError: (err) => {
      // Otra etapa activa ya es el arribo: lo avisamos en el campo sin cerrar.
      if (err instanceof ApiError && err.status === 409) return form.setError('esArribo', { message: err.message });
      onError(mensaje(err, 'No se pudo guardar la etapa.'));
    },
  });
  const err = (k: keyof Values) => form.formState.errors[k]?.message as string | undefined;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" processing={mutation.isPending && 'Guardando…'}>
        <DialogHeader>
          <DialogTitle>{item ? 'Editar etapa' : 'Nueva etapa'}</DialogTitle>
          <DialogDescription>
            Cada importación nueva copia las etapas activas, en este orden. Después se pueden ajustar en la
            importación.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">
                Nombre <span className="text-primary">*</span>
              </Label>
              <Input id="nombre" variant="filled" placeholder="Fabricación" autoFocus {...form.register('nombre')} />
              {err('nombre') && <p className="text-xs font-medium text-destructive">{err('nombre')}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="orden">Orden</Label>
                <Input id="orden" variant="filled" inputMode="numeric" {...form.register('orden')} />
                {err('orden') && <p className="text-xs font-medium text-destructive">{err('orden')}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dias">
                  Días por defecto <span className="text-primary">*</span>
                </Label>
                <Input id="dias" variant="filled" inputMode="numeric" placeholder="45" {...form.register('dias')} />
                {err('dias') && <p className="text-xs font-medium text-destructive">{err('dias')}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="avisarDiasAntes">Avisar N días antes</Label>
                <Input id="avisarDiasAntes" variant="filled" inputMode="numeric" {...form.register('avisarDiasAntes')} />
                {err('avisarDiasAntes') && (
                  <p className="text-xs font-medium text-destructive">{err('avisarDiasAntes')}</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <Label htmlFor="esArribo">Es el arribo</Label>
                <Switch
                  id="esArribo"
                  checked={form.watch('esArribo')}
                  onCheckedChange={(v) => {
                    form.setValue('esArribo', v);
                    form.clearErrors('esArribo');
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                La fecha probable de llegada de la importación sale del fin de esta etapa. Sólo una etapa activa puede
                serlo.
              </p>
              {err('esArribo') && <p className="text-xs font-medium text-destructive">{err('esArribo')}</p>}
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <Label htmlFor="activo">Activa</Label>
              <Switch id="activo" checked={form.watch('activo')} onCheckedChange={(v) => form.setValue('activo', v)} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {item ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
